// pages/EarnPage.jsx - Earn tokens through jobs, sharing, and resources
// UPDATED: Dec 29, 2025 - Added real GraphQL queries for token stats (fixes B4, B5)
import React, { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { 
  Truck, MapPin, Clock, Users, Calendar, ChevronDown, ChevronUp, 
  Briefcase, TrendingUp, Award, CheckCircle, Plus, Package,
  Share2, Link, Copy, Gift, Coins, Star, Trophy, Crown
} from 'lucide-react';
import BottomNav from '../components/common/BottomNav';
import { useNotification } from '../context/NotificationContext';
import { useJobs } from '../hooks/useJobs';
import { useSkills } from '../context/SkillContext';
import { useResources } from '../context/ResourceContext';
import { useAuth } from '../context/AuthContext';
import { useTokens } from '../context/TokenContext';
import JobCard from '../components/logistics/JobCard';
import JobClaimModal from '../components/logistics/JobClaimModal';
import JobCompletionModal from '../components/logistics/JobCompletionModal';
import OpportunitiesTab from '../components/logistics/OpportunitiesTab';
import JobFilters from '../components/logistics/JobFilters';
import PlatformJobsSection from '../components/platform/PlatformJobsSection';
import ResourceRegistration from '../components/resources/ResourceRegistration';
import SignupGate from '../components/onboarding/SignupGate';
import { formatTokens } from '../utils/tokenEconomy';
import AchievementsTab from '../components/achievements/AchievementsTab';
import LeaderboardTab from '../components/achievements/LeaderboardTab';
import Tooltip, { ReferralTooltip, JobTokensTooltip } from '../components/common/Tooltip';



// GraphQL query for token earnings summary - REAL DATA
const TOKEN_EARNINGS_SUMMARY = gql`
  query TokenEarningsSummary($days: Int) {
    tokenEarningsSummary(days: $days) {
      period
      earnings {
        purchases { total count }
        referrals { total count }
        jobs { total count }
        groups { total count }
        loyalty { total count }
        feedback { total count }
        other { total count }
        total
      }
      spending {
        redemptions { total count }
        rentals { total count }
        fees { total count }
        total
      }
      netChange
    }
  }
`;

// GraphQL query for referral stats - REAL DATA
const REFERRAL_STATS = gql`
  query ReferralStats {
    referralStats {
      code
      totalReferrals
      totalEarnings
      referees {
        userId
        joinedAt
        earnings
      }
    }
  }
`;

const EarnPage = () => {
  const [activeTab, setActiveTab] = useState('share');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [jobToComplete, setJobToComplete] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const { addNotification } = useNotification();
  const { user, isLoggedIn } = useAuth();
  const { balance } = useTokens();
  const { 
    availableJobs, 
    myJobs, 
    opportunities, 
    filters, 
    setFilters, 
    claimJob, 
    completeJob,
    getJobMatchScore,
    loading 
  } = useJobs();
  const { userSkills, skills } = useSkills();
  const { userResources } = useResources();
  
  // Fetch real token earnings data from backend
  const { data: earningsData, loading: earningsLoading } = useQuery(TOKEN_EARNINGS_SUMMARY, {
    variables: { days: 30 },
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });
  
  // Fetch real referral stats from backend
  const { data: referralData, loading: referralLoading } = useQuery(REFERRAL_STATS, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network'
  });
  
  // Extract real earnings stats from query (with fallbacks)
  const earningStats = {
    totalEarned: earningsData?.tokenEarningsSummary?.earnings?.total || 0,
    fromJobs: earningsData?.tokenEarningsSummary?.earnings?.jobs?.total || 0,
    fromReferrals: earningsData?.tokenEarningsSummary?.earnings?.referrals?.total || 0,
    fromPurchases: earningsData?.tokenEarningsSummary?.earnings?.purchases?.total || 0,
    fromFeedback: earningsData?.tokenEarningsSummary?.earnings?.feedback?.total || 0,
    fromLoyalty: earningsData?.tokenEarningsSummary?.earnings?.loyalty?.total || 0,
    period: earningsData?.tokenEarningsSummary?.period || '30 days'
  };
  
  // Extract real referral stats (with fallbacks)
  const referralStats = {
    code: referralData?.referralStats?.code || '',
    totalReferrals: referralData?.referralStats?.totalReferrals || 0,
    totalEarnings: referralData?.referralStats?.totalEarnings || 0,
    referees: referralData?.referralStats?.referees || []
  };
  
  // Use real referral code from backend, or generate fallback
  const referralCode = referralStats.code || (() => {
    if (user?.id || user?.userId) {
      const userId = user.userId || user.id;
      return userId.substring(0, 4).toUpperCase() + 
             Math.random().toString(36).substring(2, 6).toUpperCase();
    }
    return '';
  })();
  
  // Show signup gate if not logged in
  if (!isLoggedIn) {
    return <SignupGate intendedDestination="/earn" />;
  }
  
  // Get unique skill categories
  const skillCategories = [...new Set(skills.map(s => s.category))];
  
  // Get filtered jobs based on active tab
  const getDisplayJobs = () => {
    switch (activeTab) {
      case 'available':
        return availableJobs;
      case 'my-jobs':
        return myJobs.filter(j => j.status !== 'completed');
      case 'opportunities':
        return opportunities;
      case 'completed':
        return myJobs.filter(j => j.status === 'completed');
      default:
        return [];
    }
  };

  const handleClaimJob = async (job) => {
    try {
      await claimJob(job.id, job.groupId);
      addNotification({
        type: 'success',
        title: 'Job Claimed!',
        message: `You've successfully claimed "${job.title}". Check My Jobs tab for details.`,
        showToast: true
      });
      setShowClaimModal(false);
      setActiveTab('my-jobs');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Claim Failed',
        message: error.message,
        showToast: true
      });
    }
  };

  const handleInitiateCompletion = (job) => {
    setJobToComplete(job);
    setShowCompletionModal(true);
  };

  const handleCompleteJob = async (completionData) => {
    try {
      await completeJob(jobToComplete.id, completionData);
      addNotification({
        type: 'success',
        title: 'Job Marked Complete!',
        message: 'Waiting for admin approval. Tokens will be distributed once approved.',
        showToast: true
      });
      setShowCompletionModal(false);
      setJobToComplete(null);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  };

  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    const matchResult = getJobMatchScore(job.id);
    if (matchResult?.qualified && activeTab === 'available') {
      setShowClaimModal(true);
    }
  };

  const handleResourceRegistration = (resource) => {
    addNotification({
      type: 'success',
      title: 'Resource Registered!',
      message: `Your ${resource.typeName} has been registered and is ${resource.verification.status === 'verified' ? 'verified' : 'pending verification'}.`,
      showToast: true
    });
  };

  const handleCopyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
    addNotification({
      type: 'success',
      title: 'Link Copied!',
      message: 'Share with friends to earn tokens when they join.',
      showToast: true
    });
  };

  const handleShareReferral = () => {
    const link = `${window.location.origin}/register?ref=${referralCode}`;
    if (navigator.share) {
      navigator.share({
        title: 'Join UniLedge',
        text: 'Join UniLedge and we both earn tokens! Save 25-40% on everyday items through community buying.',
        url: link
      });
    } else {
      handleCopyReferralLink();
    }
  };

  // Tab counts
  const tabCounts = {
    available: availableJobs.length,
    'my-jobs': myJobs.filter(j => j.status !== 'completed').length,
    opportunities: opportunities.length,
    completed: myJobs.filter(j => j.status === 'completed').length
  };

  // Count total resources
  const totalResources = Object.values(userResources).flat().length;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold">Earn Tokens</h1>
            <p className="text-yellow-100 text-sm">Share, work, and grow your balance</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5" />
              <span className="font-bold text-lg">{formatTokens(balance || 0)}</span>
            </div>
            <p className="text-xs text-yellow-100">Your Balance</p>
          </div>
        </div>
        
        {/* Quick Stats - Now uses REAL DATA */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center">
            {earningsLoading ? (
              <div className="font-bold animate-pulse">...</div>
            ) : (
              <div className="font-bold">{formatTokens(earningStats.fromReferrals)}</div>
            )}
            <div className="text-xs text-yellow-100">From Sharing</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center">
            {earningsLoading ? (
              <div className="font-bold animate-pulse">...</div>
            ) : (
              <div className="font-bold">{formatTokens(earningStats.fromJobs)}</div>
            )}
            <div className="text-xs text-yellow-100">From Jobs</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center">
            {earningsLoading ? (
              <div className="font-bold animate-pulse">...</div>
            ) : (
              <div className="font-bold">{formatTokens(earningStats.fromPurchases)}</div>
            )}
            <div className="text-xs text-yellow-100">From Buying</div>
          </div>
        </div>
        
        {/* Period indicator */}
        <div className="text-center mt-2">
          <span className="text-xs text-yellow-200">
            Last {earningStats.period}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="flex overflow-x-auto">
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'share' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('share')}
          >
            <div className="flex items-center gap-1">
              <Share2 size={16} />
              Share
            </div>
          </button>
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'available' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('available')}
          >
            <div className="flex items-center gap-1">
              <Briefcase size={16} />
              Jobs ({tabCounts.available})
            </div>
          </button>
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'my-jobs' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('my-jobs')}
          >
            <div className="flex items-center gap-1">
              <CheckCircle size={16} />
              My Jobs ({tabCounts['my-jobs']})
            </div>
          </button>
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'resources' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('resources')}
          >
            <div className="flex items-center gap-1">
              <Package size={16} />
              Assets ({totalResources})
            </div>
          </button>
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'milestones' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('milestones')}
          >
            <div className="flex items-center gap-1">
              <Trophy size={16} />
              Milestones
            </div>
          </button>
          <button
            className={`px-4 py-3 whitespace-nowrap text-sm font-medium border-b-2 ${
              activeTab === 'leaderboard' 
                ? 'border-orange-500 text-orange-600' 
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveTab('leaderboard')}
          >
            <div className="flex items-center gap-1">
              <Crown size={16} />
              Leaderboard
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {/* Share Tab */}
        {activeTab === 'share' && (
          <div className="space-y-4">
            {/* Referral Card */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg">Share & Earn</h3>
                    {/* 4.9 TOOLTIP: Referral Program Help */}
                    <ReferralTooltip />
                  </div>
                  <p className="text-sm text-gray-600">Earn 25 tokens for each friend who joins!</p>
                </div>
              </div>
              
              {/* Referral Code - Now uses REAL CODE from backend */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-500 mb-1">Your Referral Code</p>
                <div className="flex items-center justify-between">
                  {referralLoading ? (
                    <span className="text-2xl font-mono font-bold tracking-wider animate-pulse">Loading...</span>
                  ) : (
                    <span className="text-2xl font-mono font-bold tracking-wider">{referralCode}</span>
                  )}
                  <button
                    onClick={handleCopyReferralLink}
                    disabled={referralLoading}
                    className={`p-2 rounded-lg transition-colors ${
                      copySuccess ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    } ${referralLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {copySuccess ? <CheckCircle size={20} /> : <Copy size={20} />}
                  </button>
                </div>
              </div>
              
              {/* Share Button */}
              <button
                onClick={handleShareReferral}
                disabled={referralLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Share2 className="w-5 h-5" />
                Share Your Link
              </button>
            </div>
            
            {/* How It Works */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold mb-4">How Referrals Work</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-purple-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Share your link</p>
                    <p className="text-sm text-gray-600">Send to friends, family, or on social media</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-purple-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium">They sign up & buy</p>
                    <p className="text-sm text-gray-600">They get 10 bonus tokens on signup</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-purple-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium">You both earn!</p>
                    <p className="text-sm text-gray-600">You get 25 tokens + 5% of their future rewards</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Referral Stats - Now uses REAL DATA */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold mb-3">Your Referral Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  {referralLoading ? (
                    <div className="text-2xl font-bold text-purple-600 animate-pulse">...</div>
                  ) : (
                    <div className="text-2xl font-bold text-purple-600">{referralStats.totalReferrals}</div>
                  )}
                  <div className="text-xs text-gray-600">Friends Joined</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  {referralLoading ? (
                    <div className="text-2xl font-bold text-green-600 animate-pulse">...</div>
                  ) : (
                    <div className="text-2xl font-bold text-green-600">{formatTokens(referralStats.totalEarnings)}</div>
                  )}
                  <div className="text-xs text-gray-600">Tokens Earned</div>
                </div>
              </div>
              
              {/* Show referees if any */}
              {referralStats.referees && referralStats.referees.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Referrals</h4>
                  <div className="space-y-2">
                    {referralStats.referees.slice(0, 3).map((referee, index) => (
                      <div key={referee.userId || index} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          Joined {new Date(referee.joinedAt).toLocaleDateString()}
                        </span>
                        <span className="text-green-600 font-medium">
                          +{formatTokens(referee.earnings)} earned
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Available Jobs Tab */}
        {activeTab === 'available' && (
          <div className="space-y-4">
            {/* Platform Jobs */}
            <PlatformJobsSection onJobSelect={handleViewJobDetails} />
            
            {/* Filters */}
            <JobFilters 
              filters={filters}
              onFilterChange={setFilters}
              skillCategories={skillCategories}
            />
            
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : availableJobs.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg">
                <Briefcase className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-600">No jobs match your skills right now.</p>
                <p className="text-sm text-gray-500 mt-1">Check back later or verify more skills.</p>
              </div>
            ) : (
              availableJobs.map(job => {
                const matchResult = getJobMatchScore(job.id);
                return (
                  <JobCard
                    key={job.id}
                    job={job}
                    matchResult={matchResult}
                    onClaim={() => handleViewJobDetails(job)}
                    onViewDetails={() => handleViewJobDetails(job)}
                    isClaimable={true}
                  />
                );
              })
            )}
          </div>
        )}

        {/* My Jobs Tab */}
        {activeTab === 'my-jobs' && (
          <div className="space-y-4">
            {myJobs.filter(j => j.status !== 'completed').length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg">
                <Briefcase className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-600">No active jobs.</p>
                <p className="text-sm text-gray-500 mt-1">Browse available jobs to get started.</p>
                <button
                  onClick={() => setActiveTab('available')}
                  className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg font-medium"
                >
                  Find Jobs
                </button>
              </div>
            ) : (
              myJobs.filter(j => j.status !== 'completed').map(job => (
                <div key={job.id} className="bg-white rounded-lg border-2 border-orange-200 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{job.title}</h3>
                      {job.groupTitle && <p className="text-sm text-gray-600">{job.groupTitle}</p>}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-orange-600">
                        <Coins size={18} />
                        <span className="text-xl font-bold">{job.tokenReward}</span>
                        <JobTokensTooltip tokenAmount={job.tokenReward} />
                      </div>
                      <div className="text-xs text-gray-600">tokens</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock size={14} className="mr-2" />
                      <span>Claimed: {new Date(job.claimedAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin size={14} className="mr-2" />
                      <span>{job.location}</span>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="mb-4">
                    {job.status === 'claimed' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        In Progress
                      </span>
                    )}
                    {job.status === 'pending_completion' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Clock size={12} className="mr-1" />
                        Awaiting Approval
                      </span>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {job.status === 'claimed' && (
                      <button
                        onClick={() => handleInitiateCompletion(job)}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                      >
                        Mark as Complete
                      </button>
                    )}
                    {job.status === 'pending_completion' && (
                      <div className="flex-1 text-center text-sm text-gray-600 py-2">
                        Waiting for admin approval...
                      </div>
                    )}
                    <button
                      onClick={() => handleViewJobDetails(job)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                    >
                      Details
                    </button>
                  </div>
                </div>
              ))
            )}
            
            {/* Completed Jobs Section */}
            {myJobs.filter(j => j.status === 'completed').length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-700 mb-3">Completed Jobs</h3>
                {myJobs.filter(j => j.status === 'completed').map(job => (
                  <div key={job.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{job.title}</h4>
                        <p className="text-sm text-gray-600">
                          Completed {new Date(job.completedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-green-600">
                        <Coins size={16} />
                        <span className="font-bold">+{job.tokenReward}</span>
                        <JobTokensTooltip tokenAmount={job.tokenReward} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Resources/Assets Tab */}
        {activeTab === 'resources' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-bold">Your Assets</h3>
                <p className="text-sm text-gray-600">Earn tokens by sharing your resources</p>
              </div>
              <button
                onClick={() => setShowResourceModal(true)}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Plus size={16} />
                Add Asset
              </button>
            </div>
            
            {/* Asset Types */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <Truck className="w-8 h-8 text-blue-500 mb-2" />
                <h4 className="font-medium">Vehicles</h4>
                <p className="text-xs text-gray-500">Earn 30 tokens for listing</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <MapPin className="w-8 h-8 text-green-500 mb-2" />
                <h4 className="font-medium">Spaces</h4>
                <p className="text-xs text-gray-500">Earn 50 tokens for listing</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <Package className="w-8 h-8 text-purple-500 mb-2" />
                <h4 className="font-medium">Equipment</h4>
                <p className="text-xs text-gray-500">Earn 20 tokens for listing</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <Award className="w-8 h-8 text-yellow-500 mb-2" />
                <h4 className="font-medium">Certifications</h4>
                <p className="text-xs text-gray-500">Unlock higher-paying jobs</p>
              </div>
            </div>
            
            {/* Listed Assets */}
            {totalResources === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg">
                <Package className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-600">No assets listed yet.</p>
                <p className="text-sm text-gray-500 mt-1">
                  List your vehicle, space, or equipment to earn tokens.
                </p>
                <button
                  onClick={() => setShowResourceModal(true)}
                  className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg font-medium"
                >
                  Add Your First Asset
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(userResources).map(([type, resources]) =>
                  resources.map(resource => (
                    <div key={resource.id} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{resource.name || resource.typeName}</h4>
                          <p className="text-sm text-gray-600">{type}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          resource.verification?.status === 'verified' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {resource.verification?.status === 'verified' ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Milestones Tab */}
        {activeTab === 'milestones' && (
          <AchievementsTab />
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab />
        )}
      </div>

      

      {/* Modals */}
      {selectedJob && (
        <JobClaimModal
          isOpen={showClaimModal}
          onClose={() => {
            setShowClaimModal(false);
            setSelectedJob(null);
          }}
          job={selectedJob}
          matchResult={getJobMatchScore(selectedJob.id)}
          onConfirm={handleClaimJob}
        />
      )}

      {jobToComplete && (
        <JobCompletionModal
          isOpen={showCompletionModal}
          onClose={() => {
            setShowCompletionModal(false);
            setJobToComplete(null);
          }}
          job={jobToComplete}
          onConfirm={handleCompleteJob}
          isAdmin={false}
        />
      )}

      <ResourceRegistration
        isOpen={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        onSuccess={handleResourceRegistration}
      />
      
      <BottomNav />
    </div>
  );
};

export default EarnPage;